import { ElementType } from './receipt-template.model';
import { TranslateService } from '@ngx-translate/core';
export interface MergeTag {
  key: string;
  label: string;
  elementType: ElementType;
  group: string;
  placeholder: string;
}

/** Static keys used when no TranslateService is available (design-time, tests). */
export const MERGE_TAG_GROUPS = {
  CUSTOMER: 'Customer',
  JOB: 'Job',
  ITEM: 'Item',
  BARCODES_MEDIA: 'Barcodes & Media',
  DOCUMENTS: 'Documents',
};

/** Returns translated group names. Falls back to the English strings if a key is missing. */
export function getMergeTagGroups(translate: TranslateService): typeof MERGE_TAG_GROUPS {
  const t = (key: string, fallback: string) => translate.instant(key) !== key ? translate.instant(key) : fallback;
  return {
    CUSTOMER: t(translate.instant('general.headers.customer'), translate.instant('general.headers.customer')),
    JOB: t(translate.instant('general.headers.job_card'), translate.instant('general.headers.job_card')),
    ITEM: t(translate.instant('general.headers.bike'), translate.instant('general.headers.bike')),
    BARCODES_MEDIA: t(translate.instant('general.headers.barcodes') + ', ' + translate.instant('bikeshop.modal.update_job_card.tab.attachments') + ' & ' + translate.instant('bikeshop.rentals.products.image'), translate.instant('general.headers.barcodes') + ', ' + translate.instant('bikeshop.modal.update_job_card.tab.attachments') + ' & ' + translate.instant('bikeshop.rentals.products.image')),
    DOCUMENTS: t(translate.instant('bikeshop.workshop.jobcard.extras'), translate.instant('bikeshop.workshop.jobcard.extras')),
  };
}

/** A single row of table data — one string per cell. */
export type TableRow = string[];

/**
 * The data map passed to canvas elements.
 * Scalar fields use `string`; table elements use `TableRow[]`; image arrays use `string[]` (URLs).
 */
export type CanvasDataMap = Record<string, string | string[] | TableRow[]>;

/**
 * Returns the full list of merge tags with translated labels and group names.
 * Call this inside a component/service that has TranslateService available.
 * The English strings are used as fallbacks when a translation key is missing.
 */
export function getAllMergeTags(translate: TranslateService): MergeTag[] {
  const t = (key: string, fallback: string) => translate.instant(key) !== key ? translate.instant(key) : fallback;
  const g = getMergeTagGroups(translate);
  return [
    // Customer
    {
      key: 'customer_name',
      label: t(translate.instant('general.firstname'), translate.instant('general.firstname')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{' + translate.instant('general.firstname') + '}}'
    },
    {
      key: 'customer_notes',
      label: t(translate.instant('bikeshop.workshop.jobcard.customer.booking_notes'), translate.instant('bikeshop.workshop.jobcard.customer.booking_notes')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{customer_notes}}'
    },
    {
      key: 'customer_surname',
      label: t(translate.instant('general.surname'), translate.instant('general.surname')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{customer_surname}}'
    },
    {
      key: 'email',
      label: t(translate.instant('general.email'), translate.instant('general.email')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{email}}'
    },
    {
      key: 'phone_number',
      label: t(translate.instant('general.phone'), translate.instant('general.phone')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{phone_number}}'
    },
    {
      key: 'address',
      label: t(translate.instant('bikeshop.collections.address'), translate.instant('bikeshop.collections.address')),
      elementType: 'field',
      group: g.CUSTOMER,
      placeholder: '{{address}}'
    },

    // Job
    {
      key: 'custom_questions',
      label: t(translate.instant('bikeshop.workshop.jobcard.custom_questions'), translate.instant('bikeshop.workshop.jobcard.custom_questions')),
      elementType: 'table',
      group: g.JOB,
      placeholder: '{{custom_questions}}'
    },
    {
      key: 'technician',
      label: t(translate.instant('bikeshop.users.technician'), translate.instant('bikeshop.users.technician')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{technician}}'
    },
    {
      key: 'job_card_number',
      label: t(translate.instant('general.jobcard_no'), translate.instant('general.jobcard_no')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{job_card_number}}'
    },
    {
      key: 'line_items',
      label: t(translate.instant('bikeshop.modal.update_job_card.tab.quote'), translate.instant('bikeshop.modal.update_job_card.tab.quote')),
      elementType: 'table',
      group: g.JOB, placeholder: '{{line_items}}'
    },
    {
      key: 'price',
      label: t(translate.instant('bikeshop.print.total'), translate.instant('bikeshop.print.total')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{price}}'
    },
    //  { key: 'quote',             label: t('bikeshop.receipt_template.tag.quote',             'Quote'),               elementType: 'field',         group: g.JOB,             placeholder: '{{quote}}'             },
    {
      key: 'bayNo',
      label: t(translate.instant('bikeshop.receipt_template.tag.bay_number'), translate.instant('bikeshop.receipt_template.tag.bay_number')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{bayNo}}'
    },
    {
      key: 'scheduled_date',
      label: t(translate.instant('bikeshop.print.scheduled_date'), translate.instant('bikeshop.print.scheduled_date')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{scheduled_date}}'
    },
    {
      key: 'required_by_date',
      label: t(translate.instant('bikeshop.receipt_template.tag.required_by_date'), 'Required by Date'),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{required_by_date}}'
    },
    {
      key: 'thirdPartyName',
      label: t(translate.instant('bikeshop.receipt_template.tag.third_party_name'), translate.instant('bikeshop.receipt_template.tag.third_party_name')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{thirdPartyName}}'
    },
    {
      key: 'service_type',
      label: t(translate.instant('bikeshop.print.service_type'), translate.instant('bikeshop.print.service_type')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{service_type}}'
    },
    {
      key: 'status',
      label: t(translate.instant('general.status'), translate.instant('general.status')),
      elementType: 'field',
      group: g.JOB,
      placeholder: '{{status}}'
    },
    //{ key: 'third_party', label: t('bikeshop.receipt_template.tag.third_party', 'Third Party'), elementType: 'field', group: g.JOB, placeholder: '{{third_party}}' },

    // Item
    {
      key: 'item_colour',
      label: t(translate.instant('bikeshop.modal.shared.bike.color'), translate.instant('bikeshop.modal.shared.bike.color')),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_colour}}'
    },
    {
      key: 'item_description',
      label: t(translate.instant('bikeshop.print.bike_details'), translate.instant('bikeshop.print.bike_details')),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_description}}'
    },
    {
      key: 'item_name',
      label: t(translate.instant('bikeshop.modal.shared.bike.model'), translate.instant('bikeshop.modal.shared.bike.model')),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_name}}'
    },
    {
      key: 'item_serial',
      label: t(translate.instant('bikeshop.modal.shared.bike.serial_no'), translate.instant('bikeshop.modal.shared.bike.serial_no')),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_serial}}'
    },
    {
      key: 'item_size',
      label: t(translate.instant('bikeshop.modal.shared.bike.size'), 'Size'),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_size}}'
    },
    //{
    //  key: 'item_type',
    //  label: t(translate.instant('general.headers.type'), translate.instant('general.headers.type')),
    //  elementType: 'field',
    //  group: g.ITEM,
    //  placeholder: '{{item_type}}'
    //},
    {
      key: 'item_year',
      label: t(translate.instant('bikeshop.modal.shared.bike.year'), translate.instant('bikeshop.modal.shared.bike.year')),
      elementType: 'field',
      group: g.ITEM,
      placeholder: '{{item_year}}'
    },
    {
      key: 'pre_service_items',
      label: t(translate.instant('bikeshop.workshop.jobcard.checklist.pre_service_checks'), translate.instant('bikeshop.workshop.jobcard.checklist.pre_service_checks')),
      elementType: 'table',
      group: g.JOB,
      placeholder: '{{pre_service_items}}'
    },
    {
      key: 'totals',
      label: 'Totals',
      elementType: 'table',
      group: g.JOB,
      placeholder: '{{totals}}'
    },
    //{
    //  key: 'product_desc',
    //  label: t(translate.instant('general.phone'), translate.instant('general.phone')),
    //  elementType: 'field',
    //  group: g.ITEM,
    //  placeholder: '{{product_desc}}'
    //},

    // Barcodes & Media
    {
      key: 'barcode',
      label: t(translate.instant('general.headers.barcodes'), translate.instant('general.headers.barcodes')),
      elementType: 'barcode',
      group: g.BARCODES_MEDIA,
      placeholder: '{{barcode}}'
    },
    {
      key: 'images',
      label: t(translate.instant('bikeshop.modal.update_job_card.tab.attachments'), translate.instant('bikeshop.modal.update_job_card.tab.attachments')),
      elementType: 'image',
      group: g.BARCODES_MEDIA,
      placeholder: '{{images}}'
    },
    {
      key: 'line_item_barcode',
      label: t(translate.instant('bikeshop.modal.update_job_card.tab.quote') + ' (' + translate.instant('general.headers.barcodes') + ')', translate.instant('bikeshop.modal.update_job_card.tab.quote') + ' (' + translate.instant('general.headers.barcodes') + ')'),
      elementType: 'barcode-table',
      group: g.BARCODES_MEDIA,
      placeholder: '{{line_item_barcode}}'
    },
    {
      key: 'logo',
      label: t(translate.instant('bikeshop.shop_profile.shop_logo'), translate.instant('bikeshop.shop_profile.shop_logo')),
      elementType: 'image',
      group: g.DOCUMENTS,
      placeholder: '{{logo}}'
    },
    {
      key: 'qrcode',
      label: t(translate.instant('bikeshop.shop_profile.print_qr_code'), translate.instant('bikeshop.shop_profile.print_qr_code')),
      elementType: 'qrcode',
      group: g.BARCODES_MEDIA,
      placeholder: '{{qrcode}}'
    },
    {
      key: 'third_party_logo',
      label: t(translate.instant('bikeshop.receipt_template.tag.third_party_logo'), translate.instant('bikeshop.receipt_template.tag.third_party_logo')),
      elementType: 'image',
      group: g.BARCODES_MEDIA,
      placeholder: '{{third_party_logo}}'
    },

    // Documents
    {
      key: 'companyName',
      label: t(translate.instant('helprequest.store_name'), translate.instant('helprequest.store_name')),
      elementType: 'field',
      group: g.DOCUMENTS,
      placeholder: '{{companyName}}'
    },
    {
      key: 'checklist',
      label: t(translate.instant('bikeshop.workshop.jobcard.checklists'), translate.instant('bikeshop.workshop.jobcard.checklists')),
      elementType: 'table',
      group: g.JOB,
      placeholder: '{{checklist}}'
    },
    {
      key: 'external_notes',
      label: t(translate.instant('bikeshop.receipt_template.tag.external_notes'), 'External Notes'),
      elementType: 'table',
      group: g.DOCUMENTS,
      placeholder: '{{external_notes}}'
    },
    {
      key: 'internal_notes',
      label: t(translate.instant('bikeshop.receipt_template.tag.internal_notes'), 'Internal Notes'),
      elementType: 'table',
      group: g.DOCUMENTS,
      placeholder: '{{internal_notes}}'
    },
    //{
    //  key: 'terms_extra',
    //  label: t(translate.instant('general.phone'), translate.instant('general.phone')),
    //  elementType: 'field',
    //  group: g.DOCUMENTS,
    //  placeholder: '{{terms_extra}}'
    //},
  ];
}

/**
 * Static fallback used where no TranslateService is available (e.g. design-time
 * tools, unit tests).  Prefer getAllMergeTags(translate) in components/services.
 */
export const ALL_MERGE_TAGS: MergeTag[] = [
  // Customer
  { key: 'customer_name', label: 'Customer Name', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{customer_name}}' },
  { key: 'customer_notes', label: 'Customer Notes', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{customer_notes}}' },
  { key: 'customer_surname', label: 'Customer Surname', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{customer_surname}}' },
  { key: 'email', label: 'Email', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{email}}' },
  { key: 'phone_number', label: 'Phone Number', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{phone_number}}' },
  { key: 'address', label: 'Address', elementType: 'field', group: MERGE_TAG_GROUPS.CUSTOMER, placeholder: '{{address}}' },
  { key: 'companyName', label: 'Company Name', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{companyName}}' },
  { key: 'custom_questions', label: 'Custom Questions', elementType: 'table', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{custom_questions}}' },
  { key: 'technician', label: 'Technician', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{technician}}' },
  { key: 'job_card_number', label: 'Job Card Number', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{job_card_number}}' },
  { key: 'line_items', label: 'Line Items', elementType: 'table', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{line_items}}' },
  { key: 'price', label: 'Price', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{price}}' },
  { key: 'quote', label: 'Quote', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{quote}}' },
  { key: 'bayNo',          label: 'Bay Number',       elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{bayNo}}' },
  { key: 'scheduled_date', label: 'Scheduled Date', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{scheduled_date}}' },
  { key: 'required_by_date', label: 'Required by Date', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{required_by_date}}' },
  { key: 'thirdPartyName', label: 'Third Party Name', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{thirdPartyName}}' },
  { key: 'service_type', label: 'Service Type', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{service_type}}' },
  { key: 'status', label: 'Status', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{status}}' },
  { key: 'third_party', label: 'Third Party', elementType: 'field', group: MERGE_TAG_GROUPS.JOB, placeholder: '{{third_party}}' },
  { key: 'item_colour', label: 'Item Colour', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_colour}}' },
  { key: 'item_description', label: 'Item Description', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_description}}' },
  { key: 'item_name', label: 'Item Name', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_name}}' },
  { key: 'item_serial', label: 'Item Serial Number', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_serial}}' },
  { key: 'item_size', label: 'Size', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_size}}' },
  { key: 'item_type', label: 'Item Type', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_type}}' },
  { key: 'item_year', label: 'Item Year', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{item_year}}' },
  { key: 'pre_service_items', label: 'Pre-Service Items', elementType: 'table', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{pre_service_items}}' },
  { key: 'totals',            label: 'Totals',           elementType: 'table', group: MERGE_TAG_GROUPS.JOB,  placeholder: '{{totals}}' },
  { key: 'product_desc', label: 'Product Description', elementType: 'field', group: MERGE_TAG_GROUPS.ITEM, placeholder: '{{product_desc}}' },
  { key: 'barcode', label: 'Barcode', elementType: 'barcode', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{barcode}}' },
  { key: 'images', label: 'Images', elementType: 'image', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{images}}' },
  { key: 'line_item_barcode', label: 'Line Item Barcodes', elementType: 'barcode-table', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{line_item_barcode}}' },
  { key: 'logo', label: 'Logo', elementType: 'image', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{logo}}' },
  { key: 'qrcode', label: 'QR Code', elementType: 'qrcode', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{qrcode}}' },
  { key: 'third_party_logo', label: 'Third Party Logo', elementType: 'image', group: MERGE_TAG_GROUPS.BARCODES_MEDIA, placeholder: '{{third_party_logo}}' },
  { key: 'checklist', label: 'Checklist', elementType: 'table', group: MERGE_TAG_GROUPS.DOCUMENTS, placeholder: '{{checklist}}' },
  { key: 'external_notes', label: 'External Notes', elementType: 'table', group: MERGE_TAG_GROUPS.DOCUMENTS, placeholder: '{{external_notes}}' },
  { key: 'internal_notes', label: 'Internal Notes', elementType: 'table', group: MERGE_TAG_GROUPS.DOCUMENTS, placeholder: '{{internal_notes}}' },
  { key: 'terms_extra', label: 'Terms/Extra Info', elementType: 'field', group: MERGE_TAG_GROUPS.DOCUMENTS, placeholder: '{{terms_extra}}' },
];

export const SAMPLE_DATA: CanvasDataMap = {
  customer_name: 'Bob',
  customer_notes: 'Handle with care',
  customer_surname: 'Smith',
  email: 'bob.smith@example.com',
  phone_number: '+1 555 123 4567',
  address: '1 Central Park<br>New York<br>9370<br>USA',
  companyName: 'ACME Store',
  custom_questions: [
    ['__group__', 'General'],
    ['Is everything received correctly?', '__cb:1__'],
    ['Additional comments', 'Needs it as soon as possible'],
    ['__group__', 'Safety Check'],
    ['Key available?', '__cb:0__'],
    ['Lights working?', '__cb:1__'],
  ] as TableRow[],
  technician: 'Jane Doe',
  job_card_number: 'JC-00123',
  line_items: [
    ['__group__', 'Labour'],
    ['Full Service', '1', '$350.00'],
    ['__group__', 'Parts & Materials'],
    ['Cable Set', '2', '$90.00'],
    ['Chain', '1', '$60.00'],
  ],
  price: '$ 450.00',
  quote: '$ 500.00',
  totals: [
    ['Sub-total', '$ 450.00'],
    ['Tax',       '$  50.00'],
    ['Total',     '$ 500.00'],
  ] as TableRow[],
  bayNo: 'Bay 3',
  scheduled_date: '2026-03-15',
  required_by_date: '2026-03-20',
  service_type: 'Full Service',
  status: 'In Progress',
  third_party: 'Hubtiger',
  thirdPartyName: 'Hubtiger',
  item_colour: 'Red',
  item_description: 'Super Duper Item',
  item_name: 'Super Duper Item',
  item_serial: 'SN1234567890',
  item_size: 'Medium',
  item_type: '',
  item_year: '2023',
  pre_service_items: [
    ['Check Stretch', '__cb:0__'],
    ['Check Wear (F)', '__cb:1__'],
    ['Check Wear (R)', '__cb:1__'],
    ["Don't throw out old parts", '__cb:0__'],
    ['Order number', '5'],
    ['Wash', '__cb:1__'],
  ] as TableRow[], 
  product_desc: 'Super Duper Item',
  barcode: 'JC00123',
  images: ['https://hubtigerportal.azurewebsites.net/assets/hubtiger_logo_full.png'],
  line_item_barcode: [
    ['SKU-001', 'Full Service', '1', '$ 350.00'],
    ['SKU-002', 'Cable Set', '2', '$  90.00'],
    ['SKU-003', 'Labour', '1', '$  60.00'],
  ],
  logo: '',
  qrcode: 'JC-00123',
  third_party_logo: 'https://hubtigerportal.azurewebsites.net/assets/hubtiger_logo_full.png',
  checklist: [
    ['Check Stretch', '__cb:0__'],
    ['Check Wear (F)', '__cb:1__'],
    ['Check Wear (R)', '__cb:1__'],
    ["Don't throw out old parts", '__cb:0__'],
    ['Order number', '5'],
    ['Wash', '__cb:1__'],
    ['Brake condition', '__tl:Yes|T:Minor wear noted__'],
    ['Tyre condition', '__tl:No|F:Worn|R:Good__'],
    ['Chain condition', '__tl:Not applicable__'],
  ] as TableRow[],
  external_notes: [
    ['2026-07-20', 'Handle with care when opening'],
    ['2026-07-24', 'Customer prefers afternoon delivery'],
  ] as TableRow[],
  internal_notes: [
    ['2026-07-20', 'Check if replacement parts are in stock'],
    ['2026-07-24', 'Customer has frequent billing issues'],
  ] as TableRow[],
  terms_extra: 'All work guaranteed for 30 days.',
};

/**
 * Returns a copy of SAMPLE_DATA with the partner's real name and logo injected.
 * Used by the template editor when preview mode is active but no live job card
 * has been loaded — so the designer always shows the shop's own branding.
 *
 * @param partnerData  The object returned by HelpersService.getPartnerData().
 *                     Expects { Name: string; ImageURL: string }.
 *                     Falls back to the generic SAMPLE_DATA values when absent.
 */
export function getSampleData(partnerData?: any): CanvasDataMap {
  return {
    ...SAMPLE_DATA,
    companyName: partnerData?.Name  ?? 'Store name',
    logo:        partnerData?.ImageURL ?? '',
  };
}

/**
 * Converts the nested customQuestions structure from the job-card API into
 * a flat TableRow[] that the canvas table element can render.
 *
 * Encoding used in the rows:
 *   ['__group__', 'Group Name']  → rendered as a full-width group header
 *   ['Question text', '__cb:1__'] → rendered as question + checked checkbox
 *   ['Question text', '__cb:0__'] → rendered as question + unchecked checkbox
 *   ['Question text', 'Answer']   → rendered as two text cells
 */
function mapCustomQuestions(groups: any[]): TableRow[] {
  if (!groups || !Array.isArray(groups) || groups.length === 0) return [];
  const rows: TableRow[] = [];
  for (const group of groups) {
    const groupName = String(group.groupName ?? '');
    if (groupName) rows.push(['__group__', groupName]);
    for (const q of (group.customQuestions ?? [])) {
      const question = String(q.question ?? '');
      if (q.controlType === 'CHECKBOX') {
        const checked = q.response === true || q.response === 'true' || q.response === '1' || q.response === 1;
        rows.push([question, checked ? '__cb:1__' : '__cb:0__']);
      } else {
        rows.push([question, String(q.response ?? '')]);
      }
    }
  }
  return rows;
}

/**
 * Converts the flat `checklists` array from the job-card API into a TableRow[]
 * ready for the canvas table element.
 *
 * Value resolution (per item):
 *   • defaultValue !== null  → single row using defaultValue
 *   • defaultValue === null  → two rows: description + " (F)" / " (R)" using frontValue / rearValue
 *
 * Cell encoding:
 *   CHECKBOX → '__cb:1__' (checked) or '__cb:0__' (unchecked)
 *   TEXT     → the raw string value
 */
function mapChecklists(items: any[]): TableRow[] {
  if (!items || !Array.isArray(items) || items.length === 0) return [];
  const isTrue = (v: any) => v === true || v === 'true' || v === '1' || v === 1;
  const rows: TableRow[] = [];

  for (const item of items) {
    const desc = String(item.description ?? '');
    const useDefault = item.defaultValue !== null && item.defaultValue !== undefined;
    const isCb = item.controlType === 'CHECKBOX';
    const isTl = item.controlType === 'TRAFFICLIGHT';

    if (isTl) {
      // Traffic light: always a single row.
      // |T: encodes plain text (non-front/rear) — displayed without a label.
      // |F: / |R: encode front/rear text — displayed with F: / R: labels.
      const tlVal = String(item.defaultValue ?? '');
      if (item.frontRearCheck) {
        const fStr = item.frontValue ? `|F:${item.frontValue}` : '';
        const rStr = item.rearValue  ? `|R:${item.rearValue}`  : '';
        rows.push([desc, `__tl:${tlVal}${fStr}${rStr}__`]);
      } else {
        const tStr = item.frontValue ? `|T:${item.frontValue}` : '';
        rows.push([desc, `__tl:${tlVal}${tStr}__`]);
      }
      continue;
    }

    if (!item.frontRearCheck || useDefault) {
      // Single value row
      const val = useDefault ? item.defaultValue : (item.frontValue ?? '');
      rows.push([desc, isCb ? (isTrue(val) ? '__cb:1__' : '__cb:0__') : String(val ?? '')]);
    } else {
      // Separate front and rear rows
      const fVal = item.frontValue;
      const rVal = item.rearValue;
      rows.push([`${desc} (F)`, isCb ? (isTrue(fVal) ? '__cb:1__' : '__cb:0__') : String(fVal ?? '')]);
      rows.push([`${desc} (R)`, isCb ? (isTrue(rVal) ? '__cb:1__' : '__cb:0__') : String(rVal ?? '')]);
    }
  }
  return rows;
}

/**
 * Extracts notes from the AllNotes array filtered by noteType.
 * Returns a TableRow[] where each row has [date, note].
 * Date is formatted as YYYY-MM-DD (extracted from ISO datetime).
 *
 * @param allNotes Array of note objects with { id, note, noteType, createdDate }
 * @param noteType The type to filter by (e.g. 'EXTERNAL', 'INTERNAL')
 */
function mapNotesByType(allNotes: any[], noteType: string): TableRow[] {
  if (!allNotes || !Array.isArray(allNotes) || allNotes.length === 0) return [];
  return allNotes
    .filter((n: any) => n.noteType === noteType)
    .map((n: any) => {
      const date = n.createdDate ? String(n.createdDate).split('T')[0] : '';
      const note = String(n.note ?? '');
      return [date, note];
    });
}

/**
 * Maps the response from ServiceRequestService.getManageJobCardDetails() into
 * the CanvasDataMap used by canvas elements for test-print preview.
 *
 * Scalar fields → string values.
 * line_items     → TableRow[] (one row per quote line item: [description, qty, unit price, line total]).
 *
 * Field casing follows the API response: camelCase top-level, PascalCase inside
 * customer, camelCase inside selectedItem.
 */
export function mapJobCardToSampleData(jobcard: any, statusList?: any[], partnerData?: any, translate?: TranslateService): CanvasDataMap {
  const s = (v: any): string => (v != null && v !== 0 ? String(v) : '');
  const dt = (v: any): string => v ? String(v).split('T')[0] : '';
  /** Formats a number to 2 decimal places with thousands separators (e.g. 1 234.50). */
  const n2 = (v: any): string => {
    if (v == null) return '0.00';
    const fixed = Number(v).toFixed(2);
    const [integer, decimals] = fixed.split('.');
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + decimals;
  };

  const c = jobcard?.customer ?? {};   // customer object (PascalCase)
  const item = jobcard?.selectedItem ?? {};   // bike/item object (camelCase)

  // Resolve human-readable status label from the provided status list.
  // Falls back to the raw statusID string when no list is supplied.
  const statusLabel: string = statusList
    ? (statusList.find((st: any) => st.id === jobcard?.statusID)?.label ?? s(jobcard?.statusID))
    : s(jobcard?.statusID);

  // Unique service type names
  const svcTypes: string[] = [...new Set<string>(
    (jobcard?.selectedServiceTypes ?? []).map((t: any) => t.serviceType as string),
  )];

  // Quote total from line items (for the scalar price/quote fields)
  const lineItems: any[] = jobcard?.quote?.lineItems ?? [];
  const currency = jobcard?.currencySymbol ?? '';

  const subTotal = lineItems.reduce((sum: number, li: any) =>
    sum + ((li.UnitPrice ?? li.UnitPrice_ExcludingTax ?? 0) * (li.Quantity ?? 1)), 0);
  const totalIncTax = lineItems.reduce((sum: number, li: any) =>
    sum + ((li.UnitPrice_IncludingTax ?? 0) * (li.Quantity ?? 1)), 0);
  const tax = totalIncTax - subTotal;

  const priceStr = totalIncTax > 0 ? n2(totalIncTax) : '';

  // Totals summary table: sub-total (ex tax), tax, total (inc tax)
  const hideTax = partnerData?.ShowZeroTaxAmounts === false && Math.round(tax * 100) === 0;
  const totalsRows: TableRow[] = lineItems.length > 0 ? [
    ['Sub-total', currency + n2(subTotal)],
    ...(hideTax ? [] : [['Tax', currency + n2(tax)] as TableRow]),
    ['Total',     currency + n2(totalIncTax)],
  ] : [];

  // Line items split into Labour and Parts & Materials groups
  const labourLabel = translate ? translate.instant('bikeshop.workshop.jobcard.labour') : 'Labour';
  const partsLabel  = translate ? translate.instant('bikeshop.workshop.jobcard.parts_and_materials') : 'Parts & Materials';
  const labourItems = lineItems.filter((li: any) => !!li.MarkedAsLabourProduct);
  const partsItems  = lineItems.filter((li: any) => !li.MarkedAsLabourProduct);

  const toLineRow = (li: any): TableRow => {
    const desc = s(li.Description ?? li.ItemName ?? li.ItemDescription ?? li.Name);
    const qty = s(li.Quantity ?? 1);
    const lineTotal = currency + n2((li.UnitPrice_IncludingTax ?? 0) * (li.Quantity ?? 1));
    return [desc, qty, lineTotal];
  };

  const lineItemRows: TableRow[] = [
    ...(labourItems.length > 0 ? [['__group__', labourLabel] as TableRow, ...labourItems.map(toLineRow)] : []),
    ...(partsItems.length  > 0 ? [['__group__', partsLabel]  as TableRow, ...partsItems.map(toLineRow)]  : []),
  ];

  return {
    // ── Customer ──────────────────────────────────────────────────────────
    customer_name: s(c.Name),
    customer_surname: s(c.Surname),
    email: s(c.Email),
    phone_number: s(c.PhoneNumber),
    customer_notes: s(jobcard?.clientNotes),
    address: (() => {
      // Address lives at jobcard.address (top-level, camelCase) as an object
      // with mixed-case sub-fields (Street_Number, Address_Line_1, City, etc.).
      const addr = jobcard?.address;
      if (!addr || typeof addr !== 'object') return '';
      const get = (...keys: string[]) => {
        for (const k of keys) { const v = s(addr[k]); if (v) return v; }
        return '';
      };
      // The address form saves the city into State_Province_Region too, so skip it when it matches.
      const city = get('City', 'city');
      const state = get('State_Province_Region', 'state_Province_Region', 'stateProvinceRegion');
      const parts = [
        [
          get('Street_Number', 'street_Number', 'streetNumber'),
          get('Address_Line_1', 'address_Line_1', 'addressLine1'),
        ].filter(Boolean).join(' '),
        get('Address_Line_2', 'address_Line_2', 'addressLine2'),
        get('Apartment', 'apartment'),
        city,
        state.toLowerCase() === city.toLowerCase() ? '' : state,
        get('Postal_Code', 'postal_Code', 'postalCode'),
        get('Country_Code', 'country_Code', 'countryCode', 'Country', 'country'),
      ].filter(Boolean);
      return parts.join('<br>');
    })(),

    // ── Job ───────────────────────────────────────────────────────────────
    bayNo: s(jobcard?.bayNo),
    companyName: s(jobcard?.companyName) || s(partnerData?.Name),
    job_card_number: "#" + s(jobcard?.jobCardNo),
    status: statusLabel,
    scheduled_date: dt(jobcard?.scheduleDate),
    required_by_date: dt(jobcard?.requiredByDate),
    service_type: svcTypes.join(', '),
    technician: s(jobcard?.technicianFullName || jobcard?.technicianName),
    price: currency + priceStr,
    quote: currency + priceStr,
    totals: totalsRows,
    third_party: s(jobcard?.thirdPartyName),
    thirdPartyName: s(jobcard?.thirdPartyName),
    custom_questions: mapCustomQuestions(jobcard?.customQuestions),
    line_items: lineItemRows,

    // ── Item ──────────────────────────────────────────────────────────────
    item_name: s(item.model),
    item_colour: s(item.colour),
    item_serial: s(item.serialNumber),
    item_size: s(item.bikeSize),
    item_description: [s(item.manufacturer), s(item.model)].filter(Boolean).join(' '),
    item_type: s(item.typeID),
    item_year: item.modelYear ? s(item.modelYear) : '',
    pre_service_items: mapChecklists((jobcard?.checklists ?? []).filter((c: any) => c.isPreServiceCheck === true)),
    product_desc: [s(item.manufacturer), s(item.model)].filter(Boolean).join(' '),

    // ── Barcodes & Media ──────────────────────────────────────────────────
    barcode: "#" + s(jobcard?.jobCardNo),
    qrcode: s(jobcard?.qrCodeURL),
    images: '',
    // Each row: [sku (barcode value), description, qty, line total]
    // Falls back to description when no SKU field is present on the line item.
    line_item_barcode: lineItems.map((li: any) => {
      const sku = s(li.ProductCode ?? li.SKU ?? li.Sku ?? li.BarCode ?? li.Barcode ?? li.ItemCode ?? li.Code ?? '');
      const desc = s(li.Description ?? li.ItemName ?? li.ItemDescription ?? li.Name ?? '');
      const qty = s(li.Quantity ?? 1);
      const total = currency + n2((li.UnitPrice_IncludingTax ?? 0) * (li.Quantity ?? 1));
      return [sku || desc, desc, qty, total];
    }),
    logo: s(jobcard?.companyLogo) || s(partnerData?.ImageURL),
    third_party_logo: s(jobcard?.thirdPartyLogo),

    // ── Documents ─────────────────────────────────────────────────────────
    checklist: mapChecklists((jobcard?.checklists ?? []).filter((c: any) => c.isPreServiceCheck !== true)),
    external_notes: mapNotesByType(jobcard?.allNotes, 'EXTERNAL'),
    internal_notes: mapNotesByType(jobcard?.allNotes, 'INTERNAL'),
    terms_extra: '',
  };
}
